/**
 * RAM — Change Rate Index (CD).
 *
 * POST /v3/maint/acct/change-rate-index
 *
 * Updates the rate-index code on a CD (or other rate-bearing product). The
 * new index takes effect on the next interest cycle. Rate-index catalog is
 * sourced from the IRA config bundle (Wave 5.1) when available, falling
 * back to CoreDirectorCodes filtered by `RateIndex` domain, then finally
 * to a free-text input when neither bundle has rows seeded yet.
 */
import { useState } from 'react';
import { Banner, Button, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import { useMaintActionSubmit } from '@/modules/Maint/useMaintActionSubmit';
import { emitDisclosureRedeliveryRequired } from '../disclosureTriggers';
import type { RamTarget } from '../types';

export interface ChangeRateIndexProps {
  target: RamTarget;
  onDone: () => void;
  onCancel: () => void;
}

interface RateIndexOption {
  code: string;
  label: string;
}

export default function ChangeRateIndex({
  target,
  onDone,
  onCancel,
}: ChangeRateIndexProps): JSX.Element {
  const client = useApiClient();
  const { user } = useAuth();
  const { config } = useConfig();
  const { submit, submitting, error } = useMaintActionSubmit();
  const [newRateIndex, setNewRateIndex] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [done, setDone] = useState<boolean>(false);
  const [approvalNote, setApprovalNote] = useState<string | null>(null);

  // 1) Wave 5.1 ConfigBundle: iraConfig rows tagged configType==='rate-index'.
  const iraRateIndexes: RateIndexOption[] = (config?.iraConfig ?? [])
    .filter((row) => row.configType === 'rate-index' && row.rateIndex)
    .map((row) => ({
      code: row.rateIndex!.indexCode,
      label: row.rateIndex!.displayName,
    }));

  // 2) CoreDirectorCodes catalog filtered to RateIndex domain (admin-config'd
  //    via Admin → Reference → Codes). Wave 11 plumb-through.
  const cdRateIndexes: RateIndexOption[] = (config?.coreDirectorCodes ?? [])
    .filter((row) => row.domain === 'RateIndex' && row.isActive)
    .slice()
    .sort((a, b) => {
      const ao = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
      const bo = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
      if (ao !== bo) return ao - bo;
      return a.code.localeCompare(b.code);
    })
    .map((row) => ({ code: row.code, label: row.label }));

  // Prefer IRA-config rows (richer metadata for IRA products); fall back to
  // CoreDirectorCodes for non-IRA CDs and other rate-bearing products.
  const options: RateIndexOption[] = iraRateIndexes.length > 0 ? iraRateIndexes : cdRateIndexes;
  const useFreeText = options.length === 0;

  const canSave = !submitting && Boolean(target.acctId) && newRateIndex.trim().length > 0;

  const handleSave = async (): Promise<void> => {
    if (!canSave || !target.acctId) return;
    const result = await submit(
      (b) => client.retail.maintActions.submitChangeRateIndex(b),
      {
        accountNumber: target.acctId,
        newRateIndex: newRateIndex.trim(),
        reason: reason.trim() || undefined,
      },
    );
    if (!result.success) return;
    // Reg DD §1030.5 advance-notice — rate-index change requires RATE_SCHED
    // + CD_TIS redelivery before the new rate takes effect.
    void emitDisclosureRedeliveryRequired(client, {
      action: 'change-rate-index',
      cifId: target.cifId,
      accountNumber: target.acctId,
      actorUpn: user?.upn,
      payload: { newRateIndex: newRateIndex.trim() },
    });
    if (result.data?.approvalRequired) {
      setApprovalNote(
        `Submitted — awaiting ${result.data.approverRole ?? 'approver'} approval (scenario: ${result.data.approvalScenario ?? 'default'}).`,
      );
    } else if (result.data?.testModeSkipped) {
      setApprovalNote('Test-mode — jXchange call skipped. Audit row recorded.');
    } else {
      setApprovalNote(null);
    }
    setDone(true);
  };

  if (done) {
    return (
      <div className="ram-wizard">
        <Banner tone="ok">
          Rate-index change submitted for account {target.acctId}.
        </Banner>
        {approvalNote && <Banner tone="info">{approvalNote}</Banner>}
        <div className="ram-wizard__actions">
          <Button variant="primary" onClick={onDone}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="ram-wizard">
      <div className="ram-wizard__header">
        <h2 className="ram-wizard__title">Change rate index</h2>
        <Pill variant="info">CIF {target.cifId}</Pill>
        {target.acctId && <Pill variant="muted">Account {target.acctId}</Pill>}
      </div>

      <Banner tone="info">
        Rate-index changes apply to CDs and other rate-bearing products. The new
        index takes effect on the next interest cycle.
      </Banner>

      <div className="ram-wizard__fields">
        <label className="ram-search__label">
          New rate index
          {useFreeText ? (
            <>
              <input
                className="ram-input"
                value={newRateIndex}
                onChange={(e) => setNewRateIndex(e.target.value.toUpperCase())}
                placeholder="PRIME"
                maxLength={32}
              />
              <span className="ram-search__hint">
                Rate index code (e.g. PRIME, TBILL, JUMBO)
              </span>
            </>
          ) : (
            <select
              className="ram-input"
              value={newRateIndex}
              onChange={(e) => setNewRateIndex(e.target.value)}
            >
              <option value="">— Select rate index —</option>
              {options.map((opt) => (
                <option key={opt.code} value={opt.code}>
                  {opt.label} ({opt.code})
                </option>
              ))}
            </select>
          )}
        </label>
        <label className="ram-search__label">
          Reason
          <textarea
            className="ram-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={512}
          />
        </label>
      </div>

      {error && <Banner tone="err">{error}</Banner>}

      <div className="ram-wizard__actions">
        <Button variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
          {submitting ? 'Submitting…' : 'Submit rate-index change'}
        </Button>
      </div>
    </div>
  );
}
